import Cookies from 'js-cookie';
import localForage from 'localforage';
import { store, persistor } from '../redux/store';
import { setAuthToken } from './index';
import HttpService from '../services/httpService';

export const CACHE_CLEAR_WINDOW_MS = 120000;

export const CACHE_DIRECTIVE_POLL_MS = 30000;

export const formatCountdown = (ms) => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const remainingClearMs = (requestedAt, now = Date.now()) => {
    if (!requestedAt) return 0;
    return (new Date(requestedAt).getTime() + CACHE_CLEAR_WINDOW_MS) - now;
};

const PRESERVE_ON_SOFT = ['TOKEN_KEY', 'EMAIL_ID', 'VERSION_KEY', 'redirectTo'];

const clearLocalStorage = (preserveKeys = []) => {
    try {
        if (!preserveKeys.length) {
            localStorage.clear();
            return;
        }
        Object.keys(localStorage)
            .filter(key => !preserveKeys.includes(key))
            .forEach(key => localStorage.removeItem(key));
    } catch (err) {
        console.error('clearClientStorage: localStorage', err);
    }
};

const clearSessionStorage = () => {
    try {
        sessionStorage.clear();
    } catch (err) {
        console.error('clearClientStorage: sessionStorage', err);
    }
};

const clearCookies = () => {
    try {
        const all = Cookies.get() || {};
        Object.keys(all).forEach(name => {
            Cookies.remove(name);
            Cookies.remove(name, { path: '/' });
        });
    } catch (err) {
        console.error('clearClientStorage: cookies', err);
    }
};

const clearCacheStorage = async () => {
    try {
        if (typeof window !== 'undefined' && 'caches' in window) {
            const keys = await window.caches.keys();
            await Promise.all(keys.map(key => window.caches.delete(key)));
        }
    } catch (err) {
        console.error('clearClientStorage: cacheStorage', err);
    }
};

const clearPersistedStore = async () => {
    try {
        await persistor.purge();
    } catch (err) {
        console.error('clearClientStorage: persistor.purge', err);
    }
    try {
        await localForage.clear();
    } catch (err) {
        console.error('clearClientStorage: localForage.clear', err);
    }
};

/**
 * Soft clear — wipe caches, keep the session.
 *
 *
 * @param {Object}   options
 * @param {Function} options.setUserDetails bound redux action creator
 * @param {Boolean}  options.reload         reload the page afterwards (default true)
 */
export const softClear = async ({ setUserDetails, reload = true } = {}) => {
    clearLocalStorage(PRESERVE_ON_SOFT);
    clearSessionStorage();
    clearCookies();
    await clearCacheStorage();

    const userDetails = store.getState()?.user?.userDetails;

    if (userDetails && userDetails._id && typeof setUserDetails === 'function') {
        try {
            const userAccess = await HttpService.getUserNavDeatails(userDetails._id);
            setUserDetails({ ...userDetails, canAccess: userAccess.data });
        } catch (err) {
            console.error('clearClientStorage: refreshing menu access', err);
        }
    }

    if (reload) window.location.reload();
};

/**
 * Hard clear — wipe everything, including the login token, and send the user to the login screen.
 * Purging the persisted store is safe here because the session is ending.
 *
 * @param {Object}   options
 * @param {Function} options.dispatch        redux dispatch (optional)
 * @param {Function} options.logoutUser      logout action creator (optional)
 * @param {Function} options.setUserDetails  bound redux action creator (optional)
 * @param {Boolean}  options.redirect        navigate to the login screen afterwards (default true)
 */
export const hardClear = async ({ dispatch, logoutUser, setUserDetails, redirect = true } = {}) => {
    clearLocalStorage();
    clearSessionStorage();
    clearCookies();
    await clearCacheStorage();
    await clearPersistedStore();

    setAuthToken(null);

    try {
        if (typeof dispatch === 'function' && typeof logoutUser === 'function') dispatch(logoutUser());
        if (typeof setUserDetails === 'function') setUserDetails({});
    } catch (err) {
        console.error('clearClientStorage: resetting redux', err);
    }

    if (redirect) window.location.replace('/');
};

export default { softClear, hardClear };
